import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ApiConfig } from '../config/api';

const authApiClient = axios.create({
  baseURL: ApiConfig.apiUrl,
  withCredentials: true,
});

const apiClient = axios.create({
  baseURL: ApiConfig.apiUrl,
  timeout: ApiConfig.timeout,
  withCredentials: true,
  headers: ApiConfig.headers,
});

// Request interceptor to add Authorization header
apiClient.interceptors.request.use(
  async (config) => {
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: { resolve: () => void; reject: (reason?: any) => void }[] = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 (Unauthorized) and 429 (Too Many Requests)
    if (
      (error.response?.status !== 401 && error.response?.status !== 429) ||
      !originalRequest ||
      originalRequest.url?.includes('/auth/sign-out') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      return Promise.reject(error);
    }


    // If we're already refreshing, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve: () => resolve(undefined), reject });
      }).then(() => apiClient(originalRequest));
    }

    // Prevent infinite retry loops
    if (originalRequest._retry) {
      // If refresh failed, redirect to auth page
      if (
        (error.response?.status !== 401 && error.response?.status !== 429) ||
        !originalRequest ||
        (originalRequest.url?.includes('/auth/sign-out')) ||
        (originalRequest.url?.includes('/auth/refresh'))
      ) {
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Call refresh endpoint
      await authApiClient.post('/v1/auth/refresh');

      processQueue(null);

      // Retry the original request with new token
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError as AxiosError);

      window.dispatchEvent(new Event('authFailure'));
      // const navigate = useNavigate();
      // navigate('/auth', { replace: true });

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;